import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, X } from 'lucide-react';
import { S3Modal } from './v3/primitives';
import useAssociateSearch, { type AssociateSearcher } from '../../hooks/useAssociateSearch';
// Le modali sono condivise con l'area rappresentante, che non passa dalla
// shell admin ne dalla pagina del Centro Ticket: i due fogli scoped (.adm2 /
// .sup3, entrambi portati da S3Modal sulla radice) vanno importati qui.
import '../../styles/admin-v2.css';
import '../../styles/support-v3.css';


/**
 * Attribuzione del richiedente di un ticket nato da email il cui mittente vero
 * non era riconoscibile (inoltro della segreteria). Due strade: un contatto
 * esterno (email + nome) oppure un associato, cercato con lo stesso endpoint e
 * lo stesso debounce della modale "Nuovo ticket" (hook `useAssociateSearch`).
 *
 * Il backend la consente UNA SOLA VOLTA: la conferma parte da qui e da qui
 * partono anche le notifiche al richiedente, finora sospese.
 */

export interface SetRequesterPayload {
  type: 'guest' | 'associate';
  email?: string;
  nome?: string;
  associateId?: string;
}

/** Richiedente attuale, per precompilare il cambio (Task 23/C). */
export interface CurrentRequester {
  type: 'guest' | 'associate';
  email?: string;
  nome?: string;
  associateId?: string;
  /** Nominativo mostrato nel campo di ricerca dell'associato. */
  associateLabel?: string;
}

interface SetRequesterModalProps {
  busy?: boolean;
  /** Mittente grezzo della email: precompila il campo del contatto esterno. */
  defaultEmail?: string;
  /**
   * `set` = prima attribuzione (needsRequester); `change` = cambio di un
   * richiedente gia attribuito, con il permesso dedicato. Default `set`.
   */
  mode?: 'set' | 'change';
  /** Solo in `change`: il richiedente attuale precompila la modale. */
  current?: CurrentRequester;
  /** Ricerca associati alternativa (area rappresentante). */
  searchAssociates?: AssociateSearcher;
  onClose: () => void;
  onConfirm: (payload: SetRequesterPayload) => void | Promise<void>;
}

const SetRequesterModal: React.FC<SetRequesterModalProps> = ({
  busy = false,
  defaultEmail = '',
  mode = 'set',
  current,
  searchAssociates,
  onClose,
  onConfirm,
}) => {
  const change = mode === 'change';
  const pre = change ? current : undefined;
  const [tab, setTab] = useState<'guest' | 'associate'>(pre?.type === 'associate' ? 'associate' : 'guest');
  const [email, setEmail] = useState<string>(pre?.type === 'guest' ? pre.email || '' : defaultEmail);
  const [nome, setNome] = useState<string>(pre?.type === 'guest' ? pre.nome || '' : '');
  const [associateId, setAssociateId] = useState<string>(pre?.type === 'associate' ? pre.associateId || '' : '');
  const [error, setError] = useState<string>('');

  const assoc = useAssociateSearch(tab === 'associate', searchAssociates);
  // Associato attuale nel campo di ricerca, senza far partire una ricerca.
  const { selectAndClose } = assoc;
  const preLabel = pre?.type === 'associate' ? pre.associateLabel || '' : '';
  useEffect(() => {
    if (preLabel) selectAndClose(preLabel);
  }, [preLabel, selectAndClose]);

  // Durante la conferma né il click fuori né Escape chiudono: l'operazione e
  // one-shot e chiudere a meta lascerebbe l'operatore senza esito.
  const requestClose = useCallback(() => {
    if (!busy) onClose();
  }, [busy, onClose]);

  const submit = () => {
    if (tab === 'guest') {
      if (!email.trim()) {
        setError('L’email del richiedente è obbligatoria');
        return;
      }
      setError('');
      onConfirm({ type: 'guest', email: email.trim(), nome: nome.trim() || undefined });
      return;
    }
    if (!associateId.trim()) {
      setError('Seleziona l’associato richiedente');
      return;
    }
    setError('');
    onConfirm({ type: 'associate', associateId: associateId.trim() });
  };

  const tabBtn = (key: 'guest' | 'associate', label: string) => (
    <button
      key={key}
      type="button"
      aria-pressed={tab === key}
      onClick={() => {
        setTab(key);
        setError('');
      }}
      className={`s3-segbtn s3-segbtn--sm${tab === key ? ' s3-segbtn--on' : ''}`}
    >
      {label}
    </button>
  );

  return (
    <S3Modal open onClose={requestClose} labelledBy="s3-setreq-title">
      <div className="s3-mod">
        <div className="s3-mod__head">
          <h3 className="s3-h3" id="s3-setreq-title">{change ? 'Cambia richiedente' : 'Imposta richiedente'}</h3>
          <button
            type="button"
            className="s3-pill s3-pill--sm s3-pill--icon"
            onClick={onClose}
            disabled={busy}
            aria-label="Chiudi"
          >
            <X size={14} />
          </button>
        </div>

        <div className="s3-mod__body">
          {change ? (
            <p className="s3-mod__warn">
              Il nuovo richiedente riceve l’email di apertura; i messaggi già scritti a suo nome vengono
              ri-attribuiti.
            </p>
          ) : (
            <p className="s3-mod__note">
              Il mittente della email non è stato riconosciuto. Indica chi ha davvero scritto:
              da quel momento riceverà le notifiche del ticket. L’operazione si può fare una sola volta.
            </p>
          )}

          <div className="s3-seg" role="group" aria-label="Tipo di richiedente">
            {tabBtn('guest', 'Esterno')}
            {tabBtn('associate', 'Associato')}
          </div>

          {tab === 'guest' ? (
            <>
              <div className="s3-field">
                <label className="s3-label" htmlFor="s3-setreq-email">
                  Email *
                </label>
                <input
                  id="s3-setreq-email"
                  type="email"
                  className="s3-search s3-search--plain"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="nome@esempio.it"
                />
              </div>
              <div className="s3-field">
                <label className="s3-label" htmlFor="s3-setreq-nome">
                  Nome e cognome
                </label>
                <input
                  id="s3-setreq-nome"
                  type="text"
                  className="s3-search s3-search--plain"
                  value={nome}
                  onChange={(e) => setNome(e.target.value)}
                  placeholder="Facoltativo"
                />
              </div>
            </>
          ) : (
            <div className="s3-field">
              <label className="s3-label" htmlFor="s3-setreq-assoc">
                Associato *
              </label>
              <input
                id="s3-setreq-assoc"
                type="text"
                className="s3-search s3-search--plain"
                value={assoc.query}
                onChange={(e) => {
                  assoc.setQuery(e.target.value);
                  setAssociateId('');
                }}
                placeholder="Cerca per nominativo, codice, P.IVA o email…"
              />
              {assoc.results.length > 0 && (
                <div className="s3-res">
                  {assoc.results.map((a) => (
                    <button
                      key={a._id}
                      type="button"
                      onClick={() => {
                        setAssociateId(a._id);
                        assoc.selectAndClose(a.nominativo || a.codice || a._id);
                      }}
                      className="s3-res__item"
                    >
                      {a.nominativo || '—'}
                      <span className="s3-res__meta">
                        {a.codice ? ` · ${a.codice}` : ''}
                        {a.provincia ? ` · ${a.provincia}` : ''}
                      </span>
                    </button>
                  ))}
                </div>
              )}
              {assoc.searchFailed && (
                <>
                  <p className="s3-mod__note">
                    Ricerca associati non disponibile: inserisci l’ID dell’associato.
                  </p>
                  <input
                    type="text"
                    className="s3-search s3-search--plain"
                    value={associateId}
                    onChange={(e) => setAssociateId(e.target.value)}
                    placeholder="ID associato (24 caratteri)"
                    aria-label="ID associato"
                  />
                </>
              )}
              {associateId && !assoc.searchFailed && (
                <p className="s3-mod__note">Associato selezionato.</p>
              )}
            </div>
          )}

          {error && <p className="s3-mod__err">{error}</p>}
        </div>

        <div className="s3-mod__foot">
          <button type="button" className="s3-pill" onClick={onClose} disabled={busy}>
            Annulla
          </button>
          <button type="button" className="s3-pill s3-pill--primary" onClick={submit} disabled={busy}>
            {busy && <Loader2 size={14} className="s3-spin" />} Conferma
          </button>
        </div>
      </div>
    </S3Modal>
  );
};

export default SetRequesterModal;
